import { FC } from 'react';

import { XIcon } from '@heroicons/react/outline';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';

import { CouponStats } from './types';

interface CouponDetailHeaderProps {
  stats: CouponStats;
  orderId?: string;
  order?: GiftOrderForAdmin;
  previewCopied: boolean;
  setPreviewCopied: (v: boolean) => void;
  onClose: () => void;
  /** 이용안내 일괄 발송 버튼 노출 여부 (admin · 신청자 번호 보유 쿠폰 있을 때) */
  canSendUsageGuide?: boolean;
  /** 이용안내 발송 모달 열기 */
  onSendUsageGuide?: () => void;
  /** 발송완료 번호 엑셀 버튼 노출 여부 (신청자 번호 보유 쿠폰 있을 때) */
  canExportDeliveredPhones?: boolean;
  /** 발송완료 번호 엑셀 내보내기 */
  onExportDeliveredPhones?: () => void;
  /** 미발송 포함(번호 기입 폼) 버튼 노출 여부 (미발송 쿠폰이 있을 때) */
  canExportPhoneForm?: boolean;
  /** 미발송 포함 번호 기입 폼 엑셀 내보내기 */
  onExportPhoneForm?: () => void;
  /** 대리 일괄발송 버튼 노출 여부 (admin · 미발송 쿠폰 있을 때) */
  canProxyDeliver?: boolean;
  /** 대리 일괄발송 모달 열기 */
  onProxyDeliver?: () => void;
}

export const CouponDetailHeader: FC<CouponDetailHeaderProps> = ({
  stats,
  orderId,
  order,
  previewCopied,
  setPreviewCopied,
  onClose,
  canSendUsageGuide,
  onSendUsageGuide,
  canExportDeliveredPhones,
  onExportDeliveredPhones,
  canExportPhoneForm,
  onExportPhoneForm,
  canProxyDeliver,
  onProxyDeliver,
}) => {
  return (
    <div className="flex shrink-0 items-center justify-between px-5 py-3">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-bold text-gray-900">쿠폰 상세내역</h2>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>
            전체 <span className="font-semibold text-gray-700">{stats.total}</span>
            {stats.additionalTotal > 0 && (
              <span className="ml-0.5 font-semibold text-amber-600">
                (+{stats.additionalTotal}재발행)
              </span>
            )}
            {stats.reissuedCount > 0 && (
              <span className="ml-0.5 font-semibold text-teal-600">
                (+{stats.reissuedCount}재발급)
              </span>
            )}
          </span>
          {stats.disposedCount > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span>
                폐기{' '}
                <span className="font-semibold text-slate-500">
                  {stats.disposedCount}
                </span>
              </span>
            </>
          )}
          <span className="text-gray-300">|</span>
          <span>
            미공유{' '}
            <span
              className={`font-semibold ${
                stats.notShared > 0 ? 'text-orange-600' : 'text-gray-400'
              }`}
            >
              {stats.notShared}
            </span>
          </span>
          <span className="text-gray-300">|</span>
          <span>
            미사용{' '}
            <span
              className={`font-semibold ${
                stats.available > 0 ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              {stats.available}
            </span>
          </span>
          <span className="text-gray-300">|</span>
          <span>
            교환{' '}
            <span
              className={`font-semibold ${
                stats.delivered > 0 ? 'text-green-600' : 'text-gray-400'
              }`}
            >
              {stats.delivered}
            </span>
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {canProxyDeliver && (
          <button
            onClick={onProxyDeliver}
            className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
            title="답례인사 발송과 같은 번호 목록(신청자 확인 번호)에 신청 절차 없이 상품을 바로 발송완료 처리 — 번호 없는 쿠폰·나중에받기 제외"
          >
            대리 일괄발송
          </button>
        )}
        {canExportDeliveredPhones && (
          <button
            onClick={onExportDeliveredPhones}
            className="rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
            title="신청자 번호(나중에받기·번호인증·발송완료)를 가운데 4자리 마스킹해 엑셀로 내보내기"
          >
            발송완료 번호 엑셀
          </button>
        )}
        {canExportPhoneForm && (
          <button
            onClick={onExportPhoneForm}
            className="rounded-lg border border-amber-200 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50"
            title="미발송 쿠폰까지 순번 그대로 포함해 내보내기 — 미발송 건은 수령번호 칸에 '▶ 번호 기입' 표시가 있어 채워서 회신받는 폼으로 쓸 수 있어요 (상태 열 필터로 미발송만 골라보기 가능)"
          >
            번호 기입폼 (미발송 포함)
          </button>
        )}
        {canSendUsageGuide && (
          <button
            onClick={onSendUsageGuide}
            className="rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
            title="신청자 번호가 있는 쿠폰에 교환·잔액 유의사항(이용안내)을 LMS로 발송"
          >
            이용안내 발송
          </button>
        )}
        {orderId && order && (
          <button
            onClick={() => {
              const name = encodeURIComponent(order.receiverName ?? '');
              const phone = encodeURIComponent(order.receiverPhoneNumber ?? '');
              const key = encodeURIComponent(
                process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY ?? '',
              );
              const bugoId = order.bugo?._id;
              const bugoQuery = bugoId
                ? `&bugoId=${encodeURIComponent(bugoId)}`
                : '';
              const url = `${window.location.origin}/gift-store/order/${orderId}/coupon?name=${name}&phoneNumber=${phone}&secretKey=${key}${bugoQuery}`;
              navigator.clipboard.writeText(url).then(() => {
                setPreviewCopied(true);
                setTimeout(() => setPreviewCopied(false), 2000);
              });
            }}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              previewCopied
                ? 'bg-green-50 text-green-600'
                : 'text-indigo-600 hover:bg-indigo-50'
            }`}
          >
            {previewCopied ? '복사됨!' : '미리보기 URL 복사'}
          </button>
        )}
        <button
          onClick={onClose}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};
